import React, { useState, useEffect, useMemo } from 'react';
import { ComplianceTemplate, EvidenceType, ReferenceDocument, uploadReferenceDocument, calculateRiskWeight } from '@/firebase/complianceService';
import { FileText, Image, X, Upload } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { OWNER_ROLES } from '@/constants/roles';
import { getOwnerRolesForOrganizationType } from '@/constants/organizationRoles';

interface ComplianceFormProps {
  template?: ComplianceTemplate;
  onSubmit: (template: Partial<ComplianceTemplate>) => Promise<void>;
  onCancel: () => void;
  categories: string[];
  zIndex?: number;
}

// Predefined options
const DEFAULT_FREQUENCIES = [
  { value: 'P1Y', label: 'Annually (P1Y)' },
  { value: 'P6M', label: 'Every 6 Months (P6M)' },
  { value: 'P3M', label: 'Every 3 Months (P3M)' },
  { value: 'P1M', label: 'Monthly (P1M)' },
  { value: 'P2Y', label: 'Every 2 Years (P2Y)' },
  { value: 'P5Y', label: 'Every 5 Years (P5Y)' }
];

const EVIDENCE_TYPES = [
  'pdf',
  'photo',
  'log',
  'certificate',
  'report',
  'inspection',
  'manifest'
];

const JURISDICTIONS = [
  'County/Fire Marshal',
  'OSHA/Fire Code',
  'NFPA/Local AHJ',
  'Health Dept./Insurer',
  'Water Authority',
  'State Elevator Program',
  'State/Insurer',
  'ANSI/ADA',
  'NFPA 110',
  'City',
  'County Health Dept.',
  'State ABC',
  'State Labor',
  'Federal/OSHA',
  'County Health',
  'City/County',
  'Corporate/Voluntary',
  'Insurer/State',
  'Best Practice/Insurer',
  'State/Company Policy',
  'County Health Dept.'
];

const ComplianceForm: React.FC<ComplianceFormProps> = ({ template, onSubmit, onCancel, categories, zIndex = 120 }) => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<ComplianceTemplate>>({
    name: '',
    category: '',
    frequency: '',
    lead_times_days: [],
    owner_role: '',
    evidence_types: [],
    pass_fail_criteria: '',
    jurisdiction: '',
    jurisdiction_notes: '',
    risk_level: 'Medium',
    risk_weight: 0.6, // Default for Medium
    referenceDocuments: [],
    mark_due_in_days: 30
  });

  const [evidenceType, setEvidenceType] = useState<Partial<EvidenceType>>({ 
    type: '', 
    label: '',
    required: false
  });

  const [alertDays, setAlertDays] = useState('');
  const [uploadingReference, setUploadingReference] = useState(false);

  useEffect(() => {
    if (template) {
      const riskWeight = template.risk_weight ?? calculateRiskWeight(template.risk_level);
      setFormData({
        name: template.name,
        category: template.category,
        frequency: template.frequency,
        lead_times_days: template.lead_times_days,
        owner_role: template.owner_role,
        evidence_types: template.evidence_types,
        pass_fail_criteria: template.pass_fail_criteria,
        jurisdiction: template.jurisdiction,
        jurisdiction_notes: template.jurisdiction_notes,
        risk_level: template.risk_level,
        risk_weight: riskWeight,
        referenceDocuments: template.referenceDocuments || [],
        mark_due_in_days: template.mark_due_in_days ?? 30
      });
    }
  }, [template]);

  const addAlertTime = () => {
    if (alertDays && !isNaN(Number(alertDays)) && Number(alertDays) > 0) {
      setFormData({
        ...formData,
        lead_times_days: [...(formData.lead_times_days || []), Number(alertDays)]
      });
      setAlertDays('');
    }
  };

  const removeAlertTime = (index: number) => {
    setFormData({
      ...formData,
      lead_times_days: formData.lead_times_days?.filter((_, i) => i !== index)
    });
  };

  const addEvidenceType = () => {
    if (evidenceType.type && evidenceType.label) {
      setFormData({
        ...formData,
        evidence_types: [...(formData.evidence_types || []), evidenceType as EvidenceType]
      });
      setEvidenceType({ type: '', label: '', required: false });
    }
  };

  const removeEvidenceType = (index: number) => {
    setFormData({
      ...formData,
      evidence_types: formData.evidence_types?.filter((_, i) => i !== index)
    });
  };

  const handleReferenceDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingReference(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        try {
          // Pass owner_role from formData if available (for new templates) or from template (for existing templates)
          const ownerRole = formData.owner_role || template?.owner_role;
          const referenceDoc = await uploadReferenceDocument(file, {
            templateId: template?.id,
            organizationId: template?.organizationId || currentOrganization?.id,
            uploadedBy: user
              ? { id: user.id, email: user.email, name: user.name }
              : undefined,
            title: file.name,
            source: 'web',
            additionalMetadata: ownerRole ? { owner_role: ownerRole } : undefined,
          });
          return referenceDoc;
        } catch (error: any) {
          console.error(`Error uploading ${file.name}:`, error);
          alert(`Failed to upload ${file.name}: ${error.message}`);
          return null;
        }
      });

      const uploadedDocs = (await Promise.all(uploadPromises)).filter(doc => doc !== null) as ReferenceDocument[];
      
      if (uploadedDocs.length > 0) {
        setFormData({
          ...formData,
          referenceDocuments: [...(formData.referenceDocuments || []), ...uploadedDocs]
        });
      }
    } catch (error) {
      console.error('Error uploading reference documents:', error);
    } finally {
      setUploadingReference(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const removeReferenceDocument = (index: number) => {
    setFormData({
      ...formData,
      referenceDocuments: formData.referenceDocuments?.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Require at least one alert time (lead time in days)
    if (!formData.lead_times_days || formData.lead_times_days.length === 0) {
      alert('Please add at least one alert time (days) before saving.');
      return;
    }
    // Ensure mark_due_in_days is set (default to 30 if not provided)
    if (!formData.mark_due_in_days || formData.mark_due_in_days < 0) {
      alert('Please enter a valid number of days for "Mark due for compliance in (days)".');
      return;
    }
    // Ensure risk_weight is always calculated from risk_level
    const finalFormData = {
      ...formData,
      mark_due_in_days: formData.mark_due_in_days ?? 30,
      risk_weight: formData.risk_weight ?? calculateRiskWeight(formData.risk_level || 'Medium')
    };
    await onSubmit(finalFormData);
  };

  const frequencyOptions = useMemo(() => {
    const options = [...DEFAULT_FREQUENCIES];
    const frequenciesToEnsure = [template?.frequency, formData.frequency];

    frequenciesToEnsure.forEach((freq) => {
      if (freq && !options.some(option => option.value === freq)) {
        options.push({
          value: freq,
          label: `${freq} (Custom)`,
        });
      }
    });

    return options;
  }, [template?.frequency, formData.frequency]);

  return (
    <div className={`fixed inset-y-0 right-0 w-[600px] bg-white shadow-2xl border-l border-gray-200 flex flex-col`} style={{ zIndex }}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <h2 className="text-lg font-semibold text-gray-800">
          {template ? 'Edit Compliance' : 'Add New Compliance'}
        </h2>
        <button
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700 p-1"
          title="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Form Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
          <form id="compliance-form" onSubmit={handleSubmit} className="p-4 space-y-3">
            {/* Row 1: Name and Category */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Compliance Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  placeholder="e.g., Annual Fire Inspection"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                >
                  <option value="">Select...</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 2: Frequency, Owner Role, Risk Level */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Frequency *
                </label>
                <select
                  required
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                >
                  <option value="">Select...</option>
                  {frequencyOptions.map(freq => (
                    <option key={freq.value} value={freq.value}>{freq.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Owner Role *
                </label>
                <select
                  required
                  value={formData.owner_role}
                  onChange={(e) => setFormData({ ...formData, owner_role: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                >
                  <option value="">Select...</option>
                  {(() => {
                    const organizationType = currentOrganization?.industryType || null;
                    const availableOwnerRoles = getOwnerRolesForOrganizationType(organizationType);
                    return availableOwnerRoles.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ));
                  })()}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Risk Level *
                </label>
                <select
                  required
                  value={formData.risk_level}
                  onChange={(e) => {
                    const riskLevel = e.target.value;
                    const riskWeight = calculateRiskWeight(riskLevel);
                    setFormData({ 
                      ...formData, 
                      risk_level: riskLevel,
                      risk_weight: riskWeight
                    });
                  }}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
            </div>

            {/* Mark Due For Compliance In X Days */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Mark due for compliance in (days) *
              </label>
              <input
                type="number"
                required
                min="0"
                value={formData.mark_due_in_days ?? 30}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  mark_due_in_days: e.target.value ? Number(e.target.value) : 30 
                })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                placeholder="e.g., 30 (compliance will be due 30 days from assignment)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Number of days from assignment when compliance should first be due. After that, frequency will determine subsequent due dates.
              </p>
            </div>

            {/* Alert Times (Lead Times) */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Alert Times (days) *
              </label>
              <div className="flex gap-2 mb-1">
                <input
                  type="number"
                  min="1"
                  value={alertDays}
                  onChange={(e) => setAlertDays(e.target.value)}
                  placeholder="Days"
                  className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                />
                <button
                  type="button"
                  onClick={addAlertTime}
                  className="btn-primary px-3 py-1.5 text-sm"
                >
                  Add
                </button>
              </div>
              {formData.lead_times_days && formData.lead_times_days.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {formData.lead_times_days.map((time, index) => (
                    <span key={index} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs flex items-center gap-1">
                      {time} days
                      <button
                        type="button"
                        onClick={() => removeAlertTime(index)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {(!formData.lead_times_days || formData.lead_times_days.length === 0) && (
                <p className="text-xs text-red-600 mt-1">At least one alert time is required.</p>
              )}
            </div>

            {/* Evidence Types */}
            <div>
              <div className="flex items-start justify-between gap-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Evidence Types
                </label>
              </div>
              <p className="text-xs text-red-600 mb-2">
                Tip: You can also attach specific assets, checklists, forms, or contracts to this compliance.
                Use those tools when you need structured proof instead of uploading files here.
              </p>
              <div className="space-y-1.5 mb-1.5">
                <div className="flex gap-1.5 items-center">
                  <select
                    value={evidenceType.type}
                    onChange={(e) => setEvidenceType({ ...evidenceType, type: e.target.value })}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  >
                    <option value="">Type...</option>
                    {EVIDENCE_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={evidenceType.label}
                    onChange={(e) => setEvidenceType({ ...evidenceType, label: e.target.value })}
                    placeholder="Label"
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  />
                  <label className="flex items-center whitespace-nowrap text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={evidenceType.required}
                      onChange={(e) => setEvidenceType({ ...evidenceType, required: e.target.checked })}
                      className="mr-1"
                    />
                    Req.
                  </label>
                  <button
                    type="button"
                    onClick={addEvidenceType}
                    className="btn-primary px-2 py-1.5 text-sm"
                    disabled={!evidenceType.type || !evidenceType.label}
                  >
                    Add
                  </button>
                </div>
                {formData.evidence_types && formData.evidence_types.length > 0 && (
                  <div className="space-y-1">
                    {formData.evidence_types.map((evidence, index) => (
                      <div key={index} className="bg-gray-100 px-2 py-1 rounded text-xs flex items-center justify-between">
                        <span>
                          <strong>{evidence.label}</strong> ({evidence.type})
                          {evidence.required && <span className="text-red-600 ml-1">*</span>}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeEvidenceType(index)}
                          className="text-gray-600 hover:text-gray-800 ml-2"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Row 3: Pass/Fail Criteria and Jurisdiction */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Pass/Fail Criteria *
                </label>
                <textarea
                  required
                  value={formData.pass_fail_criteria}
                  onChange={(e) => setFormData({ ...formData, pass_fail_criteria: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  rows={2}
                  placeholder="Describe pass/fail..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Jurisdiction *
                </label>
                <select
                  required
                  value={formData.jurisdiction}
                  onChange={(e) => setFormData({ ...formData, jurisdiction: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                >
                  <option value="">Select...</option>
                  {Array.from(new Set(JURISDICTIONS)).map(jurisdiction => (
                    <option key={jurisdiction} value={jurisdiction}>{jurisdiction}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Jurisdiction Notes */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Jurisdiction Notes
              </label>
              <textarea
                value={formData.jurisdiction_notes}
                onChange={(e) => setFormData({ ...formData, jurisdiction_notes: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                rows={2}
                placeholder="Additional notes..."
              />
            </div>

            {/* Reference Documents */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Reference Documents
              </label>
              <label className="flex items-center justify-center w-full px-2 py-2 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-primary-500 hover:bg-gray-50 transition-colors">
                <div className="flex flex-col items-center">
                  {uploadingReference ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mb-1"></div>
                  ) : (
                    <Upload className="w-4 h-4 text-gray-500 mb-1" />
                  )}
                  <span className="text-xs text-gray-600">
                    {uploadingReference ? 'Uploading...' : 'Upload PDF/Images'}
                  </span>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="application/pdf,image/*"
                  multiple
                  onChange={handleReferenceDocumentUpload}
                  disabled={uploadingReference}
                />
              </label>
              {formData.referenceDocuments && formData.referenceDocuments.length > 0 && (
                <div className="space-y-1 mt-1.5 max-h-32 overflow-y-auto">
                  {formData.referenceDocuments.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-1.5 bg-blue-50 rounded border border-blue-200 text-xs">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        {doc.type === 'pdf' ? (
                          <FileText className="w-3 h-3 text-red-600 flex-shrink-0" />
                        ) : (
                          <Image className="w-3 h-3 text-green-600 flex-shrink-0" />
                        )}
                        <span className="font-medium text-gray-900 truncate">{doc.name}</span>
                        <span className="text-gray-500 flex-shrink-0">({doc.type.toUpperCase()})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeReferenceDocument(index)}
                        className="text-red-600 hover:text-red-800 p-0.5 flex-shrink-0"
                        title="Remove"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
      </div>

      {/* Footer with buttons */}
      <div className="border-t border-gray-200 bg-gray-50 p-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary px-4 py-2 text-sm"
        >
          Cancel
        </button>
        <button
          type="submit"
          form="compliance-form"
          className="btn-primary px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!formData.lead_times_days || formData.lead_times_days.length === 0}
        >
          {template ? 'Update' : 'Add Compliance'}
        </button>
      </div>
    </div>
  );
};

export default ComplianceForm;
